import { createSlice } from '@reduxjs/toolkit';
import type {
  FeatureToggles,
  WhitelabelSlug,
  IUiVisibility,
  IPaymentMethod,
} from '@shore/types';
import {
  defaultUiVisibility,
  getUiVisibility,
} from '../utils/default-ui-visibility';
import {
  appointmentUsageFetched,
  initializeSuccess,
  updateTrialPeriod,
} from '../actions';
import { type AppointmentUsage } from '../features/bookings-upgrade/usage';
import {
  resolveWsbPlan,
  type WsbPlan,
} from '../features/bookings-upgrade/plan';

export interface CurrentMerchantState {
  id: string | null;
  name: string;
  whiteLabel: WhitelabelSlug | null;
  createdAt: string | null;
  isSelfSignUp: boolean;
  subscriptionStatus: string | undefined;
  subscriptionPartner: string | undefined;
  subscriptionPackageCode: string | undefined;
  subscriptionActivationAllowed: boolean;
  isSuspendedForNonPayment: boolean;
  trialEndsOn: string | null;
  trialDaysLeft: number | null;
  featureToggles: FeatureToggles;
  source: string;
  uiVisibility: IUiVisibility;
  category: string;
  currency: string;
  isTest: boolean;
  packageCode: string | null;
  pixelId: string | null;
  phoneNumber: string | null;
  sessionRole: string | null;
  paymentSettings: {
    paymentMethods: IPaymentMethod[];
  } | null;
  timeZone: string | null;
  address: {
    city: string;
    country: string;
    line1: string;
    postal_code: string;
    state: string | null;
  } | null;
  /**
   * Which WSB plan the merchant is on, read from the allowance their
   * subscription carries. `null` for merchants who are not on WSB. See
   * `features/bookings-upgrade/plan.ts` for the three plans and what separates
   * them.
   *
   * Kept next to the usage it explains, because on Portfolio there is no usage:
   * that plan includes no appointments at all, so nothing is ever fetched for
   * it and this field is the only thing that says so.
   */
  wsbPlan: WsbPlan | null;
  /**
   * Monthly appointment allowance and spend, resolved once at bootstrap. `null`
   * means uncapped: the merchant is not on WSB, or the allowance could not be
   * resolved — Core fails open in the same situations. A positive `limit` is
   * what makes a merchant eligible for the appointment-limit upsell.
   *
   * Resolved at bootstrap, then replaced as appointments are created — pushed
   * from subscription_service, because a create in the booking widget reaches
   * the shell no other way.
   */
  appointmentUsage: AppointmentUsage | null;
}

const initialState: CurrentMerchantState = {
  id: null,
  name: '',
  whiteLabel: null,
  createdAt: null,
  isSelfSignUp: false,
  subscriptionStatus: undefined,
  subscriptionPartner: undefined,
  subscriptionPackageCode: undefined,
  subscriptionActivationAllowed: false,
  isSuspendedForNonPayment: false,
  trialEndsOn: null,
  trialDaysLeft: null,
  featureToggles: {},
  source: '',
  uiVisibility: defaultUiVisibility,
  category: '',
  currency: 'EUR',
  isTest: false,
  packageCode: null,
  pixelId: null,
  phoneNumber: null,
  sessionRole: null,
  paymentSettings: null,
  timeZone: null,
  wsbPlan: null,
  appointmentUsage: null,
  address: null,
};

const merchantSlice = createSlice({
  name: 'currentMerchant',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(initializeSuccess, (state, action) => {
        const {
          currentMerchantData,
          packageCode,
          sessionRole,
          subscription,
          paymentSettings,
          appointmentUsage,
        } = action.payload;

        const org = currentMerchantData.organization;

        return {
          ...state,
          id: currentMerchantData.id,
          name: currentMerchantData.name,
          createdAt: currentMerchantData.created_at,
          whiteLabel: org?.whitelabel_slug ?? null,
          isSelfSignUp: org?.self_sign_up ?? false,
          subscriptionStatus: subscription?.status || undefined,
          subscriptionPackageCode: subscription?.plan?.id || undefined,
          subscriptionPartner: subscription?.customer?.partner || undefined,
          subscriptionActivationAllowed:
            subscription?.activation_allowed ?? false,
          isSuspendedForNonPayment:
            Boolean(currentMerchantData.suspended) &&
            currentMerchantData.suspension_reason === 'non_payment',
          trialEndsOn: subscription?.trial_end ?? org?.trial_ends_on ?? null,

          trialDaysLeft: subscription
            ? (subscription.trial_days_left ?? null)
            : (org?.trial_days_left ?? null),
          isTest: org?.test ?? false,
          packageCode: packageCode ?? org?.package_code ?? null,

          featureToggles: currentMerchantData.feature_toggles ?? {},
          source: currentMerchantData.source,
          category: currentMerchantData.category,
          currency: currentMerchantData.currency,
          address: currentMerchantData.address ?? null,
          uiVisibility: getUiVisibility(currentMerchantData),
          pixelId: currentMerchantData.pixel_id,
          phoneNumber: currentMerchantData.phone || '',
          timeZone: currentMerchantData.time_zone ?? null,
          wsbPlan: resolveWsbPlan(
            currentMerchantData.source,
            subscription?.limits?.monthly_appointments_creation_limit
          ),
          appointmentUsage: appointmentUsage ?? null,
          sessionRole,
          paymentSettings: paymentSettings
            ? { paymentMethods: paymentSettings.payment_methods || [] }
            : null,
        };
      })
      .addCase(appointmentUsageFetched, (state, action) => {
        // Assigned unconditionally: a lower count is the monthly reset, not a
        // stale message — every refresh is a read, so none can arrive out of
        // order against what it replaces.
        state.appointmentUsage = action.payload;
      })
      .addCase(updateTrialPeriod, (state, action) => {
        state.trialEndsOn = action.payload;
      });
  },
});

export const currentMerchantReducer = merchantSlice.reducer;
